
import { computed, ComputedRef, defineComponent, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { dispatchError } from "../../utils/error_handling";
import {
  checkDailyDecoinLimit,
  dispatchSuccessBox
} from "../../components/mixins";
import ContentControlButton from "./ContentControlButton.vue";
import { Content } from "./helpers";
import router from "../../../../src/routes";
import { wrapLoading } from "../../utils/wrap_loading";

export default defineComponent({
  components: {
    ContentControlButton
  },
  setup() {
    const store = useStore();

    onMounted(async () => await reloadControlState());

    const currentContent: ComputedRef<Content | null> = computed(
      () => store.getters["content_module/currentContent"]
    );

    const contents: ComputedRef<Content[]> = computed(
      () => store.getters["content_module/contents"]
    );

    watch(currentContent, async () => await reloadControlState());

    watch(contents, async () => await reloadControlState());

    const reloadControlState = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/control/reload");
      } catch (e) {
        console.error("[reloadControlState] Error ", e);
        await dispatchError(e);
      }
    };

    const hasPrevious: ComputedRef<boolean> = computed(
      () => store.getters["content_module/control/hasPrevious"]
    );

    const hasNext: ComputedRef<boolean> = computed(
      () => store.getters["content_module/control/hasNext"]
    );

    const isNextLocked: ComputedRef<boolean> = computed(
      () => store.getters["content_module/control/isNextLocked"]
    );

    const onClickPrevious = async (): Promise<void> => {
      try {
        await loadPreviousContent();
      } catch (e) {
        console.error("[onClickPrevious] Error loadPreviousContent ", e);
        await dispatchError(e);
      }
    };

    const loadPreviousContent = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/control/loadPrevious");
      } catch (e) {
        console.error("[onClickPrevious] Error loadPreviousContent ", e);
        throw e;
      }
    };

    const onClickNext = async (): Promise<void> => {
      try {
        await loadNextContent();
      } catch (e) {
        console.error("[onClickNext] Error loadNextContent ", e);
        await dispatchError(e);
      }
    };

    const loadNextContent = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/control/loadNext");
      } catch (e) {
        console.error("[loadNextContent] Error dispatch ", e);
        throw e;
      }
    };

    const onClickFinish = async (): Promise<void> => {
      try {
        await wrapLoading(async () => {
          await finishContentProvider();
          await loadUser();
          await redirectToCoursePage();
          await dispatchSuccessBox("Parabéns mais um treinamento concluído!");
          await checkDailyDecoinLimit();
        });
      } catch (e) {
        console.error("[onClickFinish] Error ", e);
        await dispatchError(e);
      }
    };

    const finishContentProvider = async (): Promise<void> => {
      try {
        await store.dispatch("content_module/finishContentProvider");
      } catch (e) {
        console.error("[finishContentProvider] Error dispatch", e);
        throw e;
      }
    };

    const redirectToCoursePage = async (): Promise<void> => {
      try {
        const currentRoute = router.currentRoute;
        if (!currentRoute) {
          return;
        }
        const courseID = currentRoute.value.params.course_id;
        const eventID = currentRoute.value.params.event_id;
        const classID = currentRoute.value.params.class_id;
        if (courseID) {
          await router.push({
            name: "course",
            params: { course_id: courseID }
          });
        } else {
          await router.push({
            name: "event",
            params: { event_id: eventID, class_id: classID }
          });
        }
      } catch (e) {
        console.error("[redirectToCoursePage] Error ", e);
        throw e;
      }
    };

    async function loadUser(): Promise<void> {
      try {
        await wrapLoading(async () => {
          await store.dispatch("userProfile_module/loadUser");
        });
      } catch (e) {
        await dispatchError(e);
      }
    }

    return {
      hasNext,
      hasPrevious,
      isNextLocked,
      onClickNext,
      onClickPrevious,
      onClickFinish
    };
  }
});
